import { useSyncExternalStore } from 'react'

interface AuthStateSnapshot {
  isAuthenticated: boolean
  isLoading: boolean
  userSub: string | null
  userName: string | null
  userEmail: string | null
  hasOfflineAccess: boolean
  tokenStatus: 'unknown' | 'ready' | 'expired'
}

const OFFLINE_ACCESS_FLAG = 'auth0.offlineAccess'
const listeners = new Set<() => void>()

function readOfflineFlag(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.localStorage.getItem(OFFLINE_ACCESS_FLAG) === '1'
  } catch {
    return false
  }
}

function persistOfflineFlag(): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(OFFLINE_ACCESS_FLAG, '1')
  } catch {
    // ignore storage failures (e.g., private mode)
  }
}

let snapshot: AuthStateSnapshot = {
  isAuthenticated: false,
  isLoading: true,
  userSub: null,
  userName: null,
  userEmail: null,
  hasOfflineAccess: readOfflineFlag(),
  tokenStatus: 'unknown',
}

function notify(): void {
  for (const listener of listeners) {
    listener()
  }
}

export function setAuthState(partial: Partial<AuthStateSnapshot>): void {
  snapshot = {
    ...snapshot,
    ...partial,
  }

  if (partial.isAuthenticated === false) {
    snapshot.tokenStatus = 'unknown'
  }

  if (partial.isAuthenticated) {
    snapshot.hasOfflineAccess = true
    persistOfflineFlag()
  }

  notify()
}

export function getAuthStateSnapshot(): AuthStateSnapshot {
  return snapshot
}

export function subscribeAuthState(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useAuthStateSnapshot(): AuthStateSnapshot {
  return useSyncExternalStore(subscribeAuthState, getAuthStateSnapshot, getAuthStateSnapshot)
}

function isNavigatorOnline(): boolean {
  if (typeof navigator === 'undefined') {
    return true
  }
  return navigator.onLine
}

export function canUseRecorderOffline(): boolean {
  return !snapshot.isAuthenticated && snapshot.hasOfflineAccess && !isNavigatorOnline()
}

export function canAccessProtectedNetwork(): boolean {
  return snapshot.isAuthenticated && isNavigatorOnline()
}

export function ensureProtectedNetworkAccess(context: string): void {
  if (!snapshot.isAuthenticated) {
    throw new Error(`Sign in to ${context}`)
  }
  if (!isNavigatorOnline()) {
    throw new Error(`Connect to the internet to ${context}`)
  }
}
